// Scramble engine.
// Contract: pure function of (ctx, config). May import only from ../types, ../handicap,
// ../scoreUtils, ../playOrder and ../../types.
//
// HOW A SCRAMBLE IS STORED
//
// Everyone on the team hits, the best shot is taken, and they all play in from there — so
// the team makes ONE score on the hole. That score is recorded against every member of the
// team, not against a single captain or a synthetic team row.
//
// This looks like duplication and is not: in a scramble every player on the team genuinely
// did make that score, because they all played the same ball in. Keeping real players on
// the card is what lets a closest-to-the-pin or a long drive belong to a PERSON, which is
// the whole reason an outing runs those contests. A team modelled as one pseudo-player
// would score fine and then have nobody to award the CTP to.
//
// It also means the scorecard, the handicap allocator, and the tournament aggregation all
// keep working untouched.
//
// HANDICAPS
//
// A scramble team's handicap is a percentage blend of its members', low handicap first —
// not each player's own, which is why this engine computes its own strokes and ignores
// ctx.strokesReceived. Percentages are house rules everywhere; the defaults below are the
// commonly published ones and `handicapPercents` overrides them.

import type { FieldDescriptor, GameEngine, GameResult, RoundCtx, ScrambleConfig } from '../types';
import { emptyResult } from '../types';
import type { RoundPlayer } from '../../types';
import { allocateStrokes } from '../handicap';
import { completeHoleIndexes, ledgerToEntries, makeLedger, fmtToPar } from '../scoreUtils';

const configFields: FieldDescriptor[] = [
  { key: 'perTeam', label: 'Per player, losing team', type: 'money', default: 0, min: 0, step: 100 },
];

const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/**
 * Share of each member's course handicap that counts, low handicap first.
 *
 * These are the allocations most published scramble formats use. Groups vary them freely,
 * which is what `handicapPercents` is for. A team size with no entry falls back to an even
 * split so an unusual size still produces something defensible rather than zero.
 */
const DEFAULT_PERCENTS: Record<number, number[]> = {
  1: [100],
  2: [35, 15],
  3: [20, 15, 10],
  4: [25, 20, 15, 10],
};

export function defaultPercentsFor(teamSize: number): number[] {
  const known = DEFAULT_PERCENTS[teamSize];
  if (known) return known;
  if (teamSize <= 0) return [];
  return new Array(teamSize).fill(Math.round(100 / teamSize) / teamSize);
}

/**
 * A team's course handicap: each member's, sorted low first, weighted by its percentage.
 * Rounded once at the end — rounding each share first drifts by a stroke on a big team.
 */
export function teamCourseHandicap(
  memberHandicaps: number[],
  percents?: number[]
): number {
  if (memberHandicaps.length === 0) return 0;
  const sorted = [...memberHandicaps].sort((a, b) => a - b);
  const pct = percents?.length === sorted.length ? percents : defaultPercentsFor(sorted.length);
  const total = sorted.reduce((sum, ch, i) => sum + ch * ((pct[i] ?? 0) / 100), 0);
  return Math.round(total);
}

const teamLabelFor = (index: number, size: number): string =>
  size === 1 ? 'Team' : `Team ${index + 1}`;

export const scrambleEngine: GameEngine<ScrambleConfig> = {
  type: 'scramble',
  meta: {
    name: 'Scramble',
    blurb: 'Everyone hits, you play the best shot — one score per team, per hole.',
    minPlayers: 2,
    maxPlayers: 8,
    inputs: [],
    needsTeams: true,
  },
  configFields,
  defaultConfig(participants: RoundPlayer[]): ScrambleConfig {
    const ids = participants.map((p) => p.playerId);
    return {
      type: 'scramble',
      scoring: 'gross',
      handicapMode: 'full',
      participantIds: ids,
      // One team of everyone — the outing case. Split it in the picker for a match.
      teams: [ids],
      perTeam: 0,
    };
  },
  validate(_ctx: RoundCtx, config: ScrambleConfig): string[] {
    const errs: string[] = [];
    const teams = config.teams ?? [];
    if (teams.length === 0) {
      errs.push('A scramble needs at least one team');
      return errs;
    }
    if (teams.some((t) => t.length < 2)) {
      errs.push('Every scramble team needs at least 2 players');
    }
    const flat = teams.flat();
    if (new Set(flat).size !== flat.length) {
      errs.push('A player cannot be on two teams');
    }
    const participants = [...new Set(config.participantIds)];
    if (flat.length !== participants.length || !flat.every((id) => participants.includes(id))) {
      errs.push('Teams must include exactly the participants');
    }
    if (config.handicapPercents && teams.some((t) => t.length !== config.handicapPercents?.length)) {
      errs.push('Handicap percentages must have one entry per player on a team');
    }
    return errs;
  },
  compute(ctx: RoundCtx, config: ScrambleConfig): GameResult {
    const teams = config.teams ?? [];
    const participantIds = config.participantIds;
    if (teams.length === 0) return emptyResult('', 'scramble');

    // Complete holes ANYWHERE on the card, not a leading run — a shotgun group starts in
    // the middle and would otherwise read as having played nothing.
    const holeIdxs = completeHoleIndexes(ctx.round, participantIds);
    if (holeIdxs.length === 0) return emptyResult('', 'scramble');

    const notes: string[] = [];

    // Per team: its strokes, and the gross it made on each completed hole.
    const teamStrokes = teams.map((team) => {
      if (config.scoring !== 'net') return null;
      const chs = team.map(
        (id) => ctx.round.players.find((p) => p.playerId === id)?.courseHandicap ?? 0
      );
      const teamCh = teamCourseHandicap(chs, config.handicapPercents);
      // Stroke indexes come from the team's first member's tee; on a real course the SI
      // ranking is the same across tees, and a scramble team plays one ball anyway.
      const firstTeeId = ctx.round.players.find((p) => p.playerId === team[0])?.teeId;
      const tee = ctx.course.tees.find((t) => t.id === firstTeeId) ?? ctx.course.tees[0];
      return { teamCh, strokes: allocateStrokes(teamCh, tee.holes.map((h) => h.strokeIndex)) };
    });

    /** The one score a team made on a hole. */
    const teamGross = (team: string[], holeIdx: number, teamIdx: number): number | null => {
      const vals = team
        .map((id) => ctx.round.holes[holeIdx]?.scores[id]?.gross ?? null)
        .filter((v): v is number => v !== null);
      if (vals.length === 0) return null;
      const best = Math.min(...vals);
      // Teammates carrying different numbers means the card was entered wrong somewhere.
      // Score the best of them so the round still computes, and say so rather than
      // silently picking one.
      if (vals.some((v) => v !== best)) {
        notes.push(
          `Hole ${holeIdx + 1}: ${teamLabelFor(teamIdx, teams.length)} has different scores for teammates — took the lowest (${best}).`
        );
      }
      return best;
    };

    const holeRows: GameResult['holeRows'] = [];
    const totals = teams.map(() => ({ gross: 0, net: 0, toPar: 0 }));

    // Report holes in card order; a shotgun changes the order they were PLAYED, never what
    // the card reads afterwards.
    for (const holeIdx of [...holeIdxs].sort((a, b) => a - b)) {
      const perPlayer: Record<string, string> = {};
      const parts: string[] = [];

      teams.forEach((team, teamIdx) => {
        const gross = teamGross(team, holeIdx, teamIdx);
        if (gross === null) return;
        const strokes = teamStrokes[teamIdx]?.strokes[holeIdx] ?? 0;
        const net = gross - strokes;
        const par = ctx.course.tees[0]?.holes[holeIdx]?.par ?? 4;

        totals[teamIdx].gross += gross;
        totals[teamIdx].net += net;
        totals[teamIdx].toPar += (config.scoring === 'net' ? net : gross) - par;

        const cell = strokes > 0 ? `${gross} (n${net})` : `${gross}`;
        for (const id of team) perPlayer[id] = cell;
        parts.push(`${teamLabelFor(teamIdx, teams.length)} ${cell}`);
      });

      holeRows.push({ hole: holeIdx + 1, summary: `Hole ${holeIdx + 1}: ${parts.join(' · ')}`, perPlayer });
    }

    const effective = (i: number) => (config.scoring === 'net' ? totals[i].net : totals[i].gross);

    // Money only when there is a pot and someone to lose it to. A charity outing runs at
    // zero — the leaderboard is the point, and the ledger stays flat.
    const ledger = makeLedger(participantIds);
    const perTeam = config.perTeam ?? 0;
    if (perTeam > 0 && teams.length > 1) {
      const best = Math.min(...teams.map((_, i) => effective(i)));
      const winners = teams.filter((_, i) => effective(i) === best);
      const losers = teams.filter((_, i) => effective(i) !== best);
      if (winners.length && losers.length) {
        const perWinner = Math.round((losers.flat().length * perTeam) / winners.flat().length);
        for (const id of winners.flat()) ledger.set(id, (ledger.get(id) ?? 0) + perWinner);
        for (const id of losers.flat()) ledger.set(id, (ledger.get(id) ?? 0) - perTeam);
      }
    }

    const standings = teams
      .map((team, i) => ({
        teamLabel: teamLabelFor(i, teams.length),
        primary: fmtToPar(totals[i].toPar),
        detail:
          config.scoring === 'net'
            ? `${totals[i].gross} gross · ${totals[i].net} net (CH ${teamStrokes[i]?.teamCh ?? 0})`
            : `${totals[i].gross} gross`,
        money: team.reduce((s, id) => s + (ledger.get(id) ?? 0), 0),
      }))
      .sort((a, b) => b.money - a.money);

    if (perTeam > 0 && teams.length === 1) {
      notes.push('A pot needs at least two teams — nothing changed hands.');
    }

    return {
      gameId: '',
      type: 'scramble',
      standings,
      holeRows,
      ledger: ledgerToEntries(ledger),
      // Holes PLAYED, which is what a leaderboard means by "thru". On a shotgun this is
      // deliberately not the highest hole number reached.
      throughHole: holeIdxs.length,
      ...(notes.length ? { notes } : {}),
    };
  },
};
